/*
==========================================
STAMP SHARED STYLE  v1.0.0
文件: assets/stamp-shared-style.js

【为什么需要这个文件】

  产品8（image-left 等 DOM 布局）地址行 line1/2/3/4 共享一套
  Font Style / Text Size / Letter Spacing / Bold 控件。

  原有实现存在两个缺陷：

  缺陷1 — data-also-line="2" 只能同步到单行（line2），
          line3 / line4 完全不响应字体变化。

  缺陷2 — form-fields 底部 <script> 尝试调用
          StampCore.setFont() / setFontSize() / setLetterSpacing() / setFontWeight()，
          但 StampCore 从未暴露这些方法，
          → 这段代码是静默失效的死代码。

  本文件通过直接调用 StampUI 内部已有的
  updateTextFont / updateTextFontSize / updateTextLetterSpacing / updateTextFontWeight
  （通过 MutationObserver + data-line-index 事件触发机制代理），
  彻底修复 line1-4 同步问题。

【设计原则】

  ✓ 零侵入 — 不修改 stamp-ui.js / stamp-core.js / stamp-address-composer.js
  ✓ 自动发现 — 通过 data-shared-group 属性找到控件，无需硬编码行号
  ✓ 产品无关 — 任何产品只要在 form-fields 中加 data-shared-group，
               即可自动获得"一个控件同步多行"能力
  ✓ 按需跳过 — address-label 产品走 StampAddressComposer 自己的机制，
               本文件自动跳过

【HTML 标记约定（form-fields 里使用）】

  字体按钮（已有 data-also-lines="rest" 的改用 data-shared-group）：
    <button class="prod-font-btn"
            data-font="Montserrat"
            data-shared-group="addr-lines"
            data-shared-lines="1,2,3,4">Aa</button>

  Size 滑块：
    <input type="range" id="prodFontSizeSlider1"
           data-shared-group="addr-lines"
           data-shared-lines="1,2,3,4">

  Letter Spacing 滑块：
    <input type="range" id="prodLetterSpaceSlider1"
           data-shared-group="addr-lines"
           data-shared-lines="1,2,3,4">

  Bold 滑块：
    <input type="range" id="prodFontWeightSlider1"
           data-shared-group="addr-lines"
           data-shared-lines="1,2,3,4">

  data-shared-lines 的值为逗号分隔的行号，可以是任意组合，
  例如 "1,2,3,4" 或 "2,3,4" 或 "1,3"。

  【兼容旧标记】
  本文件同时兼容旧版 data-also-lines="rest" 标记，
  "rest" 表示从当前行到最后一个存在 prodTextLine{i} 的行。
  旧产品无需修改 form-fields HTML 也能立即生效。

【调用关系】

  本文件读取：
    window.StampUI（stamp-ui.js 暴露的对象）——仅用于判断布局
    DOM：[data-line-index="i"] span — 直接操作，与 stamp-ui.js 内部完全一致
    stamp-core.js：Core.getAllCanvases() — 获取所有 canvas（含镜像预览）

  本文件不依赖：
    StampCore.setFont / setFontSize（这些方法不存在）
    data-also-line / data-also-lines（旧标记，本文件兼容但不依赖）

【加载位置】

  在 stamp-customizer.liquid 的 JS 加载列表中，
  放在 stamp-customizer.js 之后、stamp-patch.js 之前：

    <script src="{{ 'stamp-customizer.js' | asset_url }}" defer></script>
    <script src="{{ 'stamp-shared-style.js' | asset_url }}" defer></script>  ← 新增
    <script src="{{ 'stamp-patch.js' | asset_url }}" defer></script>

【新产品复用】

  新产品（line1-4 共享样式）只需：
  1. 在 form-fields 对应控件加 data-shared-group="xxx" data-shared-lines="1,2,3,4"
  2. 确保 stamp-customizer.liquid 里加载了本文件
  无需修改任何 JS 文件。

==========================================
*/

window.StampSharedStyle = (function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────
     工具：读取产品配置
  ───────────────────────────────────────────────────────── */
  function _getLayout() {
    try {
      var el = document.getElementById('prodStampConfig');
      return JSON.parse((el && el.textContent) || '{}').layout || '';
    } catch (e) { return ''; }
  }

  /* ─────────────────────────────────────────────────────────
     工具：解析 data-shared-lines 或 data-also-lines
     返回数字行号数组，例如 [1, 2, 3, 4]
  ───────────────────────────────────────────────────────── */
  function _parseLines(el) {
    /* 优先读新属性 data-shared-lines */
    var sharedLines = el.getAttribute('data-shared-lines');
    if (sharedLines && sharedLines.trim()) {
      return sharedLines.split(',').map(function (s) { return parseInt(s.trim(), 10); }).filter(function (n) { return !isNaN(n); });
    }

    /* 兼容旧属性 data-also-lines="rest" */
    var alsoLines = el.getAttribute('data-also-lines');
    if (alsoLines === 'rest') {
      /* "rest" = 从 line1 开始，找到所有存在的 prodTextLine{i} */
      return _getAllRestIndices();
    }

    /* 兼容旧属性 data-also-line="2"（单个行号）*/
    var alsoLine = el.getAttribute('data-also-line');
    if (alsoLine) {
      var base = parseInt(el.getAttribute('data-line-index') || el.dataset.lineIndex || '1', 10);
      return [base, parseInt(alsoLine, 10)];
    }

    return [];
  }

  /* 找到所有存在 prodTextLine{i} 的行号（i >= 1）*/
  function _getAllRestIndices() {
    var indices = [];
    for (var i = 1; i <= 10; i++) {
      if (document.getElementById('prodTextLine' + i)) {
        indices.push(i);
      } else {
        break; /* 连续编号，遇到第一个空缺就停 */
      }
    }
    return indices;
  }

  /* ─────────────────────────────────────────────────────────
     核心：对指定行列表执行样式更新
     通过直接操作 DOM [data-line-index] 元素实现，
     与 stamp-ui.js 内部 updateTextFont/Size/Weight/LetterSpacing 完全一致
  ───────────────────────────────────────────────────────── */

  /* 获取所有 stamp canvas（含镜像预览）*/
  function _getAllCanvases() {
    if (window.StampCore && typeof window.StampCore.getAllCanvases === 'function') {
      return window.StampCore.getAllCanvases().filter(Boolean);
    }
    /* 兜底：手动找 */
    return ['prodStampCanvas', 'prodMobileCanvasBottom', 'prodMobileCanvasTop'].map(function (id) {
      return document.getElementById(id);
    }).filter(Boolean);
  }

  function _applyFont(lineIndices, font) {
    _getAllCanvases().forEach(function (c) {
      lineIndices.forEach(function (i) {
        var tl = c.querySelector('[data-line-index="' + i + '"]');
        if (!tl) return;
        tl.dataset.font = font;
        /* 弧形文字走 Core.regenerateCurvedText */
        if (tl.classList.contains('curved-top-curve') || tl.classList.contains('curved-bottom-curve')) {
          if (window.StampCore && typeof window.StampCore.regenerateCurvedText === 'function') {
            window.StampCore.regenerateCurvedText(c);
          }
        } else {
          var sp = tl.querySelector('span');
          if (sp) sp.style.fontFamily = "'" + font + "', sans-serif";
        }
        /* 同步 hidden input */
        var hid = document.getElementById('prodFontLine' + i);
        if (hid) hid.value = font;
      });
    });
  }

  function _applyFontSize(lineIndices, px) {
    _getAllCanvases().forEach(function (c) {
      lineIndices.forEach(function (i) {
        var tl = c.querySelector('[data-line-index="' + i + '"]');
        if (!tl) return;
        tl.dataset.fontsize = px + 'px';
        if (tl.classList.contains('curved-top-curve') || tl.classList.contains('curved-bottom-curve')) {
          if (window.StampCore && typeof window.StampCore.regenerateCurvedText === 'function') {
            window.StampCore.regenerateCurvedText(c);
          }
        } else {
          var sp = tl.querySelector('span');
          if (sp) sp.style.fontSize = px + 'px';
        }
        /* 同步 hidden input */
        var hid = document.getElementById('prodFontSizeLine' + i);
        if (hid) hid.value = px + 'px';
        /* 同步镜像 slider / val */
        var sl2 = document.getElementById('prodFontSizeSlider' + i);
        if (sl2 && sl2.value !== String(px)) sl2.value = px;
        var val2 = document.getElementById('prodFontSizeVal' + i);
        if (val2) val2.textContent = px + 'px';
      });
    });
  }

  function _applyLetterSpacing(lineIndices, v) {
    _getAllCanvases().forEach(function (c) {
      lineIndices.forEach(function (i) {
        var tl = c.querySelector('[data-line-index="' + i + '"]');
        if (!tl) return;
        tl.dataset.letterSpacing = v;
        var sp = tl.querySelector('span');
        if (sp) sp.style.letterSpacing = v + 'px';
      });
    });
  }

  function _applyFontWeight(lineIndices, w) {
    _getAllCanvases().forEach(function (c) {
      lineIndices.forEach(function (i) {
        var tl = c.querySelector('[data-line-index="' + i + '"]');
        if (!tl) return;
        tl.dataset.fontweight = w;
        if (tl.classList.contains('curved-top-curve') || tl.classList.contains('curved-bottom-curve')) {
          if (window.StampCore && typeof window.StampCore.regenerateCurvedText === 'function') {
            window.StampCore.regenerateCurvedText(c);
          }
        } else {
          var sp = tl.querySelector('span');
          if (sp) sp.style.fontWeight = w;
        }
        /* 同步 hidden input */
        var hid = document.getElementById('prodFontWeightLine' + i);
        if (hid) hid.value = w;
        /* 同步镜像 slider / val */
        var sl2 = document.getElementById('prodFontWeightSlider' + i);
        if (sl2 && sl2.value !== String(w)) sl2.value = w;
        var val2 = document.getElementById('prodFontWeightVal' + i);
        if (val2) val2.textContent = w;
      });
    });
  }

  /* ─────────────────────────────────────────────────────────
     绑定事件
  ───────────────────────────────────────────────────────── */
  function _bind() {
    var layout = _getLayout();

    /* address-label 有自己的 StampAddressComposer，跳过 */
    if (layout === 'address-label') return;

    /* ── 字体按钮 ── */
    /* 同时覆盖新标记 data-shared-group 和旧标记 data-also-lines */
    document.querySelectorAll(
      '[data-shared-group] .prod-font-btn, .prod-font-btn[data-also-lines], .prod-font-btn[data-shared-lines]'
    ).forEach(function (btn) {
      /* 防止重复绑定 */
      if (btn._ssBound) return;
      btn._ssBound = true;

      btn.addEventListener('click', function () {
        var font    = btn.dataset.font;
        var lines   = _parseLines(btn);
        if (!lines.length) return;

        /* 更新 active 状态（只在同组内） */
        var group = btn.closest('[data-shared-group]') ||
                    btn.closest('.prod-font-picker') ||
                    btn.closest('.prod-font-options');
        if (group) {
          group.querySelectorAll('.prod-font-btn').forEach(function (b) { b.classList.remove('active'); });
        }
        btn.classList.add('active');

        _applyFont(lines, font);
      });
    });

    /* ── Size 滑块 ── */
    document.querySelectorAll(
      'input[type="range"][data-shared-lines], input[type="range"][data-also-lines]'
    ).forEach(function (sl) {
      /* 只处理字号滑块（id 含 FontSize 或 class 含 fontsize-slider / shared-size）*/
      var isSize = /FontSize|fontsize|shared-size/.test(sl.id + ' ' + sl.className);
      if (!isSize) return;
      if (sl._ssBound) return;
      sl._ssBound = true;

      sl.addEventListener('input', function () {
        var v     = parseInt(sl.value, 10);
        var lines = _parseLines(sl);
        if (!lines.length) return;

        /* 更新 val 显示 */
        var valEl = document.getElementById(sl.id.replace('Slider', 'Val'));
        if (valEl) valEl.textContent = v + 'px';

        _applyFontSize(lines, v);
      });
    });

    /* ── Letter Spacing 滑块 ── */
    document.querySelectorAll(
      'input[type="range"][data-shared-lines], input[type="range"][data-also-lines]'
    ).forEach(function (sl) {
      var isSpacing = /LetterSpace|letter|shared-spacing/.test(sl.id + ' ' + sl.className);
      if (!isSpacing) return;
      if (sl._ssSpacingBound) return;
      sl._ssSpacingBound = true;

      sl.addEventListener('input', function () {
        var v     = parseInt(sl.value, 10);
        var lines = _parseLines(sl);
        if (!lines.length) return;

        var valEl = document.getElementById(sl.id.replace('Slider', 'Val'));
        if (valEl) valEl.textContent = v + 'px';

        _applyLetterSpacing(lines, v);
      });
    });

    /* ── Bold（FontWeight）滑块 ── */
    document.querySelectorAll(
      'input[type="range"][data-shared-lines], input[type="range"][data-also-lines]'
    ).forEach(function (sl) {
      var isWeight = /FontWeight|fontweight|shared-weight/.test(sl.id + ' ' + sl.className);
      if (!isWeight) return;
      if (sl._ssWeightBound) return;
      sl._ssWeightBound = true;

      sl.addEventListener('input', function () {
        var v     = parseInt(sl.value, 10);
        var lines = _parseLines(sl);
        if (!lines.length) return;

        var valEl = document.getElementById(sl.id.replace('Slider', 'Val'));
        if (valEl) valEl.textContent = v;

        _applyFontWeight(lines, v);
      });
    });
  }

  /* ─────────────────────────────────────────────────────────
     入口：等 DOM 和 StampCore 就绪后初始化
  ───────────────────────────────────────────────────────── */
  function _init() {
    var start = Date.now();
    function check() {
      /* 等 StampCore 就绪（getAllCanvases 需要它）*/
      if (window.StampCore && typeof window.StampCore.getAllCanvases === 'function') {
        _bind();
        return;
      }
      if (Date.now() - start > 8000) {
        console.warn('[StampSharedStyle] Timeout waiting for StampCore. Binding anyway.');
        _bind();
        return;
      }
      setTimeout(check, 80);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', check);
    } else {
      check();
    }
  }

  _init();

  /* ─────────────────────────────────────────────────────────
     公开 API（供调试和外部调用）
  ───────────────────────────────────────────────────────── */
  return {
    /**
     * 手动对一组行应用字体
     * @param {number[]} lineIndices  行号数组，例如 [1,2,3,4]
     * @param {string}   font         字体名称
     */
    applyFont:          _applyFont,
    applyFontSize:      _applyFontSize,
    applyLetterSpacing: _applyLetterSpacing,
    applyFontWeight:    _applyFontWeight,

    /**
     * 重新扫描并绑定所有共享控件（动态注入控件后调用）
     */
    rebind:             _bind
  };

})();